// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useEffect, useId, useRef, useState } from 'preact/hooks';
import type {
  FeeTierKey,
  FeeTiersResponse,
  QuotedFeeTier,
  SharedWithdrawalQuoteResponse,
} from '../api.js';
import { formatBtc, formatSats } from '../format.js';
import '../styles/fee-selector.css';

/**
 * COB-485: the fee-rate control. The served tier ladder plus a `Custom`
 * option, rendered as a **dropdown** whose collapsed state shows only the
 * current selection and whose open list shows every option's full data.
 *
 * # Why a dropdown and not a chip row (Ticket D, revising Ticket A)
 *
 * The first cut rendered all four tiers as a permanently-expanded chip row,
 * each chip stacking a label, a rate and a total — four to seven rendered
 * lines above the caveat, before the Custom disclosure. That is a
 * comparison table shown to every user on every send, and most users are not
 * comparing: they want the default and the button. The dropdown keeps the
 * comparison — every option still carries its rate *and* its price, and the
 * open list is the same table — but makes it on-demand, and collapses the
 * resting control to a single line carrying the selection's own data.
 *
 * What did **not** change: the served `standard` default, the validation
 * ladder (floor block / sub-minimum ack / >3× warn / ceiling block), the
 * per-surface copy, {@link FEE_TIME_CAVEAT}, the "rates as of" line, the
 * >20 % pre-submit re-confirm, and {@link FeeSelection} itself. This is a
 * presentation change; the fee *policy* is untouched.
 *
 * # Why not a native `<select>`
 *
 * A native option is a single run of text with no styling and no layout: it
 * cannot carry a label, a monospaced rate and a muted total as separate,
 * aligned data. Squeezing "Standard — 8 sat/vB — ≈ 3,296 sats" into one
 * string loses exactly the scannability the ladder exists for. So this is
 * the ARIA select-only combobox: a trigger button (`role="combobox"`,
 * `aria-expanded`, `aria-activedescendant`) over a `role="listbox"` popover
 * of `role="option"` rows, with focus staying on the trigger.
 *
 * # The browser computes no fee of its own
 *
 * Every `sat_vb` here is served already-integer, already-clamped into the
 * deployment's `[floor, ceiling]`, and already monotonic. A shared-vault
 * option's `fee_sats` is computed server-side by the same code the
 * withdrawal will be built with; a wallet option's total is passed in via
 * {@link FeeOptionFees} by the wallet's *own* planner
 * (`KeyAgent.estimateWalletSpend`). One formula per surface, always the
 * surface's own builder. The one arithmetic this module does is the
 * *custom* rate's shared-vault estimate — and even that multiplies the
 * server's `est_vsize` by the rate using the server's exact
 * `max(ceil(rate × vsize), min_fee)` form, because a second, drifting vsize
 * formula in TypeScript is exactly what COB-479 was.
 *
 * # No per-tier time claims
 *
 * The options carry a name, a rate and a price, never "may take days" /
 * "~half a day" / "next block, ~10 min". Those sub-labels shipped with the
 * first cut and were wrong in the ordinary case: the ladder is clamped by
 * the relay floor and forced monotonic, so adjacent tiers frequently resolve
 * to the *same* rate — guaranteed on the fallback ladder, common on mainnet
 * in a quiet fee market — and two identically-priced options making
 * different time promises is not imprecise, it is false. `estimatesmartfee`
 * gives a rate for a confirmation *target*, not a calibrated time, so the
 * claim was never available to us. One {@link FEE_TIME_CAVEAT} footnote sits
 * under the control instead, and the only sub-label left is the served
 * `default` marker on the default tier.
 */

/**
 * The AR-10 absolute fee ceiling, in sats — a **copy**, not an import.
 *
 * The authority is `MAX_SHARED_WITHDRAWAL_FEE_SATS` in
 * `web/key-agent/src/shared-vault.ts`, where `assertSharedWithdrawalSafe`
 * refuses to sign a withdrawal paying more; `shared_vault_routes.rs` carries
 * the server-side mirror. It is duplicated here rather than imported because
 * the bare `@cobault/key-agent` specifier is deliberately **type-only** in the
 * SPA (the agent is worker-isolated; see the alias note in `vite.config.ts`),
 * and pulling a subpath in for one integer would drag the whole signing module
 * into the main chunk.
 *
 * Purely a UX guard: it turns "the co-signers' browsers will all silently
 * refuse this" into an inline message before the withdrawal is created. Keep
 * all three copies in lock-step.
 */
export const MAX_SHARED_WITHDRAWAL_FEE_SATS = 1_000_000;

/**
 * Which surface the selector is mounted on.
 *
 * The options, the ladder, the thresholds and the whole validation ladder are
 * identical everywhere — the *only* thing this switches is the handful of
 * messages that name what is being paid for. The shared-vault floor message
 * explains itself by saying a shared-vault withdrawal cannot be re-fee'd once
 * signed, which is true there and simply the wrong product on the wallet's
 * send card. Defaults to `shared-vault` so Ticket A's call sites are
 * unchanged. (COB-485 Ticket B)
 */
export type FeeSurface = 'shared-vault' | 'wallet';

/** Which option is selected, plus the custom-rate field's own state. */
export interface FeeSelection {
  mode: FeeTierKey | 'custom';
  /** The custom rate as typed. Only meaningful when `mode === 'custom'`. */
  custom: string;
  /** The "I understand this may not confirm for a long time" acknowledgement.
   *  Required only for a custom rate below the `minimum` tier. */
  ack: boolean;
}

/**
 * The default selection: `standard`.
 *
 * Not `fast` — a next-block rate quoted now is stale by the time a human
 * signing quorum has been collected anyway. Emphatically not `minimum`, and
 * not the old "cheapest thing the box will accept" behaviour: `standard` sits
 * a comfortable margin above the relay floor, which is the incident-relevant
 * property. `default_tier` is served, so this is only the pre-fetch seed.
 */
export function initialFeeSelection(): FeeSelection {
  return { mode: 'standard', custom: '', ack: false };
}

/** Either ladder shape the selector can render: the plain served ladder, or
 *  the richer per-draft quote that also carries `fee_sats` and `est_vsize`. */
export type FeeLadderView = FeeTiersResponse | SharedWithdrawalQuoteResponse;

/** A quote carries `est_vsize`; a bare ladder does not. */
export function ladderIsQuote(
  ladder: FeeLadderView,
): ladder is SharedWithdrawalQuoteResponse {
  return 'est_vsize' in ladder;
}

export function ladderTier(
  ladder: FeeLadderView | null,
  key: FeeTierKey,
): { sat_vb: number; fee_sats?: number } | null {
  if (!ladder) return null;
  return ladder.tiers.find((t) => t.key === key) ?? null;
}

/**
 * The fee, in sats, a rate of `satVb` would pay on this draft — or `null`
 * when the ladder is not a quote (no output set yet, so no size to price).
 *
 * Mirrors `cobault_core::shared_withdrawal::shared_withdrawal_fee_sats`:
 * `max(ceil(rate × vsize), min_fee_sats)`. Kept in lock-step with it; a
 * served tier's `fee_sats` comes from that Rust function directly, so this
 * form is only ever used for a custom rate the server has not priced.
 */
export function estimatedFeeSats(
  ladder: FeeLadderView | null,
  satVb: number,
): number | null {
  if (!ladder || !ladderIsQuote(ladder)) return null;
  if (!Number.isFinite(satVb) || satVb <= 0) return null;
  return Math.max(Math.ceil(satVb * ladder.est_vsize), ladder.min_fee_sats);
}

/** "≈ 3,296 sats (≈ 0.00003296 BTC)" — sats primary, BTC secondary, no fiat
 *  (a price oracle has no business inside a fee widget). */
export function formatFeeSats(feeSats: number): string {
  return `≈ ${formatSats(feeSats)} sats (≈ ${formatBtc(feeSats)} BTC)`;
}

/** The custom field parsed to a number. `NaN` when it is not a number at all
 *  (including empty), which the validator treats as a block. */
export function customRateNumber(custom: string): number {
  const trimmed = custom.trim();
  if (trimmed === '') return NaN;
  return Number(trimmed);
}

export interface FeeValidation {
  /** `error` blocks submit. `ack` blocks until the checkbox is ticked.
   *  `warn` is advisory only. */
  kind: 'ok' | 'warn' | 'ack' | 'error';
  message: string | null;
}

/**
 * The §2.3 validation ladder for a custom rate, in the order a rate is
 * checked:
 *
 * | condition | behaviour |
 * |---|---|
 * | not a finite positive number, or below `floor_sat_vb` | **block** |
 * | above `ceiling_sat_vb`, or a derived fee above the AR-10 ceiling | **block** |
 * | at/above the floor but below the `minimum` tier | **warn + ack** |
 * | more than 3× the `fast` tier | warn (non-blocking) |
 *
 * The floor blocks and the sub-`minimum` band only warns because the ladder
 * *moves*: blocking everything under the current `minimum` tier would make
 * a rate that was fine a minute ago suddenly illegal. The floor does not
 * move — it is deployment policy, and it is the property the incident needed.
 */
export function validateCustomFeeRate(
  custom: string,
  ladder: FeeLadderView | null,
  surface: FeeSurface = 'shared-vault',
): FeeValidation {
  const n = customRateNumber(custom);
  if (!Number.isFinite(n) || n <= 0) {
    return { kind: 'error', message: 'Enter a fee rate in sat/vB.' };
  }
  if (!ladder) {
    // No ladder yet: accept nothing rather than accept anything. The form
    // cannot submit without a ladder in any case.
    return { kind: 'error', message: 'Fetching current network rates…' };
  }
  if (n < ladder.floor_sat_vb) {
    // Same block on both surfaces; only the reason names the product. A
    // wallet send is signed and broadcast in one click with no re-fee path
    // in the UI, so the consequence is the same — it is just not a quorum's
    // worth of it.
    const why =
      surface === 'wallet'
        ? "A lower rate may never confirm, and this send can't be re-fee'd once it's broadcast."
        : "A lower rate may never confirm, and a shared-vault withdrawal can't be re-fee'd once it's signed.";
    return {
      kind: 'error',
      message: `At least ${ladder.floor_sat_vb} sat/vB. ${why}`,
    };
  }
  if (n > ladder.ceiling_sat_vb) {
    return {
      kind: 'error',
      message: `At most ${ladder.ceiling_sat_vb} sat/vB.`,
    };
  }
  // AR-10 is a shared-vault rule — it exists because every co-signer's key
  // agent refuses to sign above it. A personal-wallet spend has no such
  // co-signer and no such ceiling, so the check is scoped, not merely
  // unreachable-in-practice on a bare ladder.
  const fee = surface === 'shared-vault' ? estimatedFeeSats(ladder, n) : null;
  if (fee !== null && fee > MAX_SHARED_WITHDRAWAL_FEE_SATS) {
    return {
      kind: 'error',
      message: `That's a fee of ${formatSats(fee)} sats — over the ${formatSats(
        MAX_SHARED_WITHDRAWAL_FEE_SATS,
      )}-sat maximum for a shared-vault withdrawal.`,
    };
  }
  const minimum = ladderTier(ladder, 'minimum');
  if (minimum && n < minimum.sat_vb) {
    return {
      kind: 'ack',
      message:
        'That is below the cheapest current estimate — this withdrawal may not confirm for a long time.',
    };
  }
  const fast = ladderTier(ladder, 'fast');
  if (fast && n > fast.sat_vb * 3) {
    return {
      kind: 'warn',
      message: `That is more than 3× the Fast rate (${fast.sat_vb} sat/vB). You'll overpay the miner.`,
    };
  }
  return { kind: 'ok', message: null };
}

/** The sat/vB this selection resolves to, or `null` when it cannot resolve
 *  (no ladder yet, or an unusable custom value). Never invented client-side:
 *  a tier's rate is whatever the server served for it. */
export function selectedFeeRate(
  sel: FeeSelection,
  ladder: FeeLadderView | null,
): number | null {
  if (sel.mode === 'custom') {
    const n = customRateNumber(sel.custom);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const tier = ladderTier(ladder, sel.mode);
  return tier ? tier.sat_vb : null;
}

/** The blocking reason for this selection, or `null` when it may be
 *  submitted. Folds in the ack gate: an acknowledged sub-`minimum` rate is
 *  submittable, an un-acknowledged one is not. */
export function feeSelectionError(
  sel: FeeSelection,
  ladder: FeeLadderView | null,
  surface: FeeSurface = 'shared-vault',
): string | null {
  if (!ladder) return 'Fetching current network rates…';
  if (sel.mode !== 'custom') {
    return ladderTier(ladder, sel.mode)
      ? null
      : 'Choose a fee rate.';
  }
  const v = validateCustomFeeRate(sel.custom, ladder, surface);
  if (v.kind === 'error') return v.message;
  if (v.kind === 'ack' && !sel.ack) {
    return 'Confirm you understand this rate may not confirm for a long time.';
  }
  return null;
}

/** The `fee_tier` to send with the withdrawal — observability only, and
 *  omitted for a custom rate. */
export function feeTierForRequest(sel: FeeSelection): FeeTierKey | undefined {
  return sel.mode === 'custom' ? undefined : sel.mode;
}

/**
 * §2.4: has the rate moved enough between render and submit that the user
 * must be shown the new number and re-confirm?
 *
 * More than 20 % in either direction. Below that the difference is noise
 * against a fee the user is knowingly choosing a tier for; above it, silently
 * submitting a number they never saw is the same class of surprise the free-
 * text box used to produce.
 */
export function rateMovedMaterially(before: number, after: number): boolean {
  if (!Number.isFinite(before) || before <= 0) return false;
  if (!Number.isFinite(after) || after <= 0) return false;
  return Math.abs(after - before) / before > 0.2;
}

/** "rates as of 14:32:07" — browser-local, never UTC (the admin-surface TZ
 *  convention). `null` until a ladder has been fetched. */
export function ratesAsOfLabel(asOf: number | null): string | null {
  if (asOf === null || !Number.isFinite(asOf) || asOf <= 0) return null;
  return `rates as of ${new Date(asOf * 1000).toLocaleTimeString()}`;
}

/** The notice shown when the served ladder is the static fallback — the user
 *  is told these are defaults, not estimates. */
export const FEE_FALLBACK_NOTICE =
  'Network fee estimates are unavailable right now — showing default rates.';

/** The one caveat under the control, shown for **every** rendered ladder
 *  (esplora and fallback alike). It replaces the per-tier time sub-labels: it
 *  is the honest form of what those claimed, and it stays true when two tiers
 *  price identically. */
export const FEE_TIME_CAVEAT =
  'Fee estimates are derived from network confirmation targets — actual confirmation times are not guaranteed.';

/** The re-confirm prompt after a >20 % move between render and submit. */
export function rateMovedNotice(before: number, after: number): string {
  return `Network fees moved while you were filling this in — your rate is now ${after} sat/vB (was ${before}). Check the total and create the withdrawal again to confirm.`;
}

// ── The dropdown's view model (COB-485 Ticket D) ──────────────────────
//
// Deliberately pure and exported: the rows, the option index and the
// keyboard map are the whole behaviour of the control, and the suites here
// are pure-function-first. What is left in the component is event wiring.

/** An option in the list: a served tier, or the trailing `custom` row. */
export type FeeOptionKey = FeeTierKey | 'custom';

/**
 * Per-option fee totals in sats, priced by **this surface's own builder**.
 *
 * The shared-vault surface never needs this — its quote ladder already
 * carries `fee_sats` per tier, straight from the Rust builder. The wallet
 * surface passes one: `SendCard` prices each distinct served rate through
 * `KeyAgent.estimateWalletSpend`, which runs the same `planWalletSpend` the
 * signed transaction is built from. An absent key means "this surface has no
 * honest number for that option", and the row renders its rate alone.
 */
export type FeeOptionFees = Partial<Record<FeeOptionKey, number>>;

/** The placeholder on a `Custom` row with no rate typed yet. */
export const FEE_CUSTOM_HINT = 'Enter your own rate';

/** One rendered option row — everything the trigger and the list need, and
 *  nothing about how either is drawn. */
export interface FeeOptionRow {
  key: FeeOptionKey;
  /** "Standard", "Custom". */
  label: string;
  /** The served `default` marker, or `null`. Never a time claim. */
  subLabel: string | null;
  /** "8 sat/vB", or `null` when nothing resolves (an un-typed `Custom`). */
  rate: string | null;
  /** "≈ 3,296 sats (≈ 0.00003296 BTC)", or `null` when this surface has no
   *  honest total for the option. */
  fee: string | null;
  /** Stand-in text for a row carrying neither rate nor fee. */
  hint: string | null;
}

/**
 * The option list, in served order, with `Custom` last.
 *
 * A tier's total is the served `fee_sats` when the ladder is a per-draft
 * quote, else whatever `fees` supplies for that key, else nothing — this
 * function never multiplies a rate by anything to fill a gap.
 *
 * The `Custom` row shows its rate as soon as the typed value is a positive
 * number, but shows a **total** only while the value is not blocked: pricing
 * a rate the control refuses reads as endorsement of it.
 */
export function feeOptionRows(
  ladder: FeeLadderView | null,
  selection: FeeSelection,
  opts: { fees?: FeeOptionFees | null; surface?: FeeSurface } = {},
): FeeOptionRow[] {
  if (!ladder) return [];
  const fees = opts.fees ?? null;
  const surface = opts.surface ?? 'shared-vault';

  const rows: FeeOptionRow[] = ladder.tiers.map((t) => {
    const quoted = (t as QuotedFeeTier).fee_sats;
    const feeSats = typeof quoted === 'number' ? quoted : fees?.[t.key];
    return {
      key: t.key,
      label: t.label,
      subLabel: t.sub_label ?? null,
      rate: `${t.sat_vb} sat/vB`,
      fee: typeof feeSats === 'number' ? formatFeeSats(feeSats) : null,
      hint: null,
    };
  });

  const n = customRateNumber(selection.custom);
  const hasRate =
    selection.mode === 'custom' && Number.isFinite(n) && n > 0;
  const blocked =
    selection.mode === 'custom' &&
    validateCustomFeeRate(selection.custom, ladder, surface).kind === 'error';
  const customFee =
    hasRate && !blocked
      ? (fees?.custom ?? estimatedFeeSats(ladder, n))
      : null;

  rows.push({
    key: 'custom',
    label: 'Custom',
    subLabel: null,
    rate: hasRate ? `${n} sat/vB` : null,
    fee: typeof customFee === 'number' ? formatFeeSats(customFee) : null,
    hint: hasRate ? null : FEE_CUSTOM_HINT,
  });

  return rows;
}

/** Where `mode` sits in `rows`, or `-1` when the served ladder does not carry
 *  it (an empty ladder, or a tier key the deployment stopped serving). */
export function feeOptionIndex(
  rows: readonly FeeOptionRow[],
  mode: FeeSelection['mode'],
): number {
  return rows.findIndex((r) => r.key === mode);
}

/** Shown on the trigger when nothing resolves — the same state
 *  {@link feeSelectionError} blocks submit on. */
export const FEE_TRIGGER_PLACEHOLDER = 'Choose a fee rate';

/** Which keys open a closed listbox. Enter/Space activate it as a button;
 *  the arrows and Home/End open it *and* land on the current selection,
 *  matching the ARIA select-only combobox pattern. */
export function keyOpensFeeList(key: string): boolean {
  return (
    key === 'Enter' ||
    key === ' ' ||
    key === 'Spacebar' ||
    key === 'ArrowDown' ||
    key === 'ArrowUp' ||
    key === 'Home' ||
    key === 'End'
  );
}

/**
 * Where a keypress moves the active option in an **open** list, or `null`
 * for a key the listbox does not handle (so the caller leaves the default
 * alone). Clamps at both ends rather than wrapping: a wrapping fee ladder
 * makes "cheapest" and "dearest" adjacent, which is precisely the pair a
 * mis-keyed arrow must not swap between.
 */
export function nextActiveIndex(
  current: number,
  key: string,
  count: number,
): number | null {
  if (count <= 0) return null;
  const clamp = (i: number) => Math.max(0, Math.min(count - 1, i));
  switch (key) {
    case 'ArrowDown':
      return clamp(current + 1);
    case 'ArrowUp':
      return clamp(current - 1);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/**
 * The dropdown + custom disclosure. Presentational and controlled: the page
 * owns the ladder fetch (mount, every 60 s, on window focus, and once more
 * immediately before submit) and hands the result in.
 */
export function FeeSelector({
  ladder,
  selection,
  onChange,
  disabled = false,
  surface = 'shared-vault',
  optionFees = null,
}: {
  /** The served ladder, or `null` while the first fetch is in flight. */
  ladder: FeeLadderView | null;
  selection: FeeSelection;
  onChange: (next: FeeSelection) => void;
  disabled?: boolean;
  /** Which surface this is mounted on — copy only. See {@link FeeSurface}. */
  surface?: FeeSurface;
  /** Per-option totals priced by this surface's own builder, when the ladder
   *  itself does not carry them. See {@link FeeOptionFees}. */
  optionFees?: FeeOptionFees | null;
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const baseId = useId();
  const titleId = `${baseId}-fee-title`;
  const triggerId = `${baseId}-fee-trigger`;
  const listId = `${baseId}-fee-listbox`;
  const optionId = (key: FeeOptionKey) => `${baseId}-fee-option-${key}`;

  const validation =
    selection.mode === 'custom'
      ? validateCustomFeeRate(selection.custom, ladder, surface)
      : null;
  const asOf = ratesAsOfLabel(ladder ? ladder.as_of : null);

  const rows = feeOptionRows(ladder, selection, { fees: optionFees, surface });
  const selectedIndex = feeOptionIndex(rows, selection.mode);
  const selectedRow = selectedIndex >= 0 ? rows[selectedIndex] : null;
  // The ladder refreshes underneath an open list; never point
  // `aria-activedescendant` at a row that is no longer rendered.
  const active = rows.length === 0 ? -1 : Math.min(activeIndex, rows.length - 1);
  const activeRow = active >= 0 ? rows[active] : null;

  // A disabled control must not keep a popover open over a form that is
  // mid-submit, and a ladder that vanished has nothing to list.
  useEffect(() => {
    if (disabled || rows.length === 0) setOpen(false);
  }, [disabled, rows.length]);

  // Click-outside dismiss. `mousedown`, not `click`: a press that starts
  // outside must close the list before the click lands, so the click acts on
  // what the user actually aimed at.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  function openList() {
    if (disabled || rows.length === 0) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }

  /** Close and hand focus back to the trigger — the keyboard user must not be
   *  dropped at the top of the document by an Esc. */
  function closeList(refocus = true) {
    setOpen(false);
    if (refocus) triggerRef.current?.focus();
  }

  function selectRow(index: number) {
    const row = rows[index];
    if (!row) return;
    if (row.key === 'custom') {
      // Keep whatever was typed before, exactly like the old Custom chip.
      onChange({ ...selection, mode: 'custom' });
    } else {
      // Picking a tier clears the custom value AND its acknowledgement — the
      // field is never the source of truth for which mode is active.
      onChange({ mode: row.key, custom: '', ack: false });
    }
    closeList();
  }

  function onTriggerKeyDown(event: KeyboardEvent) {
    if (disabled) return;
    if (!open) {
      if (keyOpensFeeList(event.key)) {
        // Cancels the button's own default activation too, so Enter/Space do
        // not also fire the click handler and immediately re-close.
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeList();
      return;
    }
    if (event.key === 'Tab') {
      // Let focus leave; just do not leave a popover behind it.
      setOpen(false);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      selectRow(active);
      return;
    }
    if (nextActiveIndex(active, event.key, rows.length) === null) return;
    event.preventDefault();
    // Functional update, not `setActiveIndex(next)`: two arrow presses that
    // land in the same tick (key repeat, a synthetic burst) would otherwise
    // both step off the same render's index and only move one row.
    setActiveIndex((cur) => {
      const from = Math.min(Math.max(cur, 0), rows.length - 1);
      return nextActiveIndex(from, event.key, rows.length) ?? from;
    });
  }

  return (
    <div class="fee-selector" ref={rootRef}>
      <div class="fee-selector-head">
        <span class="fee-selector-title" id={titleId}>
          Network fee
        </span>
        {asOf && <span class="fee-selector-asof tabular-nums">{asOf}</span>}
      </div>
      {ladder === null ? (
        <p class="accept-input-hint">Fetching current network rates…</p>
      ) : (
        <div class="fee-select">
          <button
            type="button"
            ref={triggerRef}
            id={triggerId}
            class="fee-select-trigger"
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={listId}
            aria-activedescendant={
              open && activeRow ? optionId(activeRow.key) : undefined
            }
            /* Name = the section label plus the trigger's own text, so the
               announcement is "Network fee, Standard 8 sat/vB ≈ 3,296 sats,
               combo box" rather than either half on its own. */
            aria-labelledby={`${titleId} ${triggerId}`}
            disabled={disabled}
            onClick={() => (open ? closeList(false) : openList())}
            onKeyDown={onTriggerKeyDown}
          >
            <span class="fee-select-value">
              {selectedRow ? (
                <>
                  <span class="fee-select-value-label">
                    {selectedRow.label}
                  </span>
                  {selectedRow.rate && (
                    <span class="fee-select-value-rate tabular-nums">
                      {selectedRow.rate}
                    </span>
                  )}
                  {selectedRow.fee && (
                    <span class="fee-select-value-fee tabular-nums">
                      {selectedRow.fee}
                    </span>
                  )}
                  {selectedRow.hint && (
                    <span class="fee-select-value-hint">
                      {selectedRow.hint}
                    </span>
                  )}
                </>
              ) : (
                <span class="fee-select-value-hint">
                  {FEE_TRIGGER_PLACEHOLDER}
                </span>
              )}
            </span>
            <span class="fee-select-caret" aria-hidden="true">
              ▾
            </span>
          </button>
          {open && (
            <ul
              class="fee-select-list"
              id={listId}
              role="listbox"
              aria-labelledby={titleId}
            >
              {rows.map((row, i) => (
                <li
                  key={row.key}
                  id={optionId(row.key)}
                  role="option"
                  aria-selected={selection.mode === row.key}
                  class={
                    i === active
                      ? 'fee-select-option fee-select-option-active'
                      : 'fee-select-option'
                  }
                  onClick={() => selectRow(i)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  <span class="fee-select-option-label">{row.label}</span>
                  {row.subLabel && (
                    <span class="fee-select-option-sub">{row.subLabel}</span>
                  )}
                  {row.rate && (
                    <span class="fee-select-option-rate tabular-nums">
                      {row.rate}
                    </span>
                  )}
                  {row.fee && (
                    <span class="fee-select-option-fee tabular-nums">
                      {row.fee}
                    </span>
                  )}
                  {row.hint && (
                    <span class="fee-select-option-hint">{row.hint}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Every rendered ladder gets the caveat, not just the fallback one:
          a live esplora ladder is exactly as incapable of promising a
          confirmation time as a static one. */}
      {ladder !== null && (
        <p class="fee-selector-caveat">{FEE_TIME_CAVEAT}</p>
      )}

      {ladder && ladder.source === 'fallback' && (
        <p class="shared-vault-notice">{FEE_FALLBACK_NOTICE}</p>
      )}

      {selection.mode === 'custom' && (
        <div class="fee-custom">
          <label class="shared-vault-fee-label">
            Fee rate (sat/vB)
            <input
              class="accept-input shared-vault-send-amount"
              type="text"
              inputMode="decimal"
              value={selection.custom}
              disabled={disabled}
              onInput={(e) =>
                onChange({
                  ...selection,
                  custom: (e.target as HTMLInputElement).value,
                  // A new value re-opens the acknowledgement: the ack is a
                  // statement about a specific rate, not a standing consent.
                  ack: false,
                })
              }
            />
          </label>
          {validation?.message && (
            <p
              class={
                validation.kind === 'error'
                  ? 'vault-card-error'
                  : 'accept-input-hint sv-allowlist-warn'
              }
            >
              {validation.message}
            </p>
          )}
          {validation?.kind === 'ack' && (
            <label class="fee-custom-ack">
              <input
                type="checkbox"
                checked={selection.ack}
                disabled={disabled}
                onChange={(e) =>
                  onChange({
                    ...selection,
                    ack: (e.target as HTMLInputElement).checked,
                  })
                }
              />
              <span>
                I understand this may not confirm for a long time.
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
}
